import { useCallback } from "react";
import axiosInstant from "../api";
import { usePagination } from "./usePagination";
import type { useCommonParams } from "./useCommonParams";

interface UseResourceListArgs {
    endpoint: string;
    pageSize: number;
    searchText: string;
    searchParams: URLSearchParams;
    setSearchParams: ReturnType<typeof useCommonParams>["setSearchParams"];
    page: number;
    setPage: (n: number) => void;
    onCountChange?: (n: number) => void;
    // The whole response body (for envelopes that carry more than rows/count,
    // e.g. totals). Called after onCountChange.
    onResponse?: (data: any) => void;
    extraParams?: Record<string, string | number | undefined>;
    errorLabel?: string;
}

export function useResourceList<T>({
    endpoint,
    pageSize,
    searchText,
    searchParams,
    setSearchParams,
    page,
    setPage,
    onCountChange,
    onResponse,
    extraParams,
    errorLabel = "Failed to load",
}: UseResourceListArgs) {
    const fetchFn = useCallback(
        async (pageNum: number) => {
            try {
                const params: Record<string, any> = { page: pageNum, limit: pageSize };
                if (searchText) params.search = searchText;
                if (extraParams) {
                    Object.entries(extraParams).forEach(([k, v]) => {
                        if (v !== undefined && v !== null && v !== "") params[k] = v;
                    });
                }
                const res = await axiosInstant.get(endpoint, { params });
                const count = res.data?.count ?? 0;
                onCountChange?.(count);
                onResponse?.(res.data);
                return { rows: (res.data?.rows ?? []) as T[], count, error: null };
            } catch (e: any) {
                const msg = e?.response?.data?.error ?? errorLabel;
                return { rows: [] as T[], count: 0, error: msg };
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [endpoint, pageSize, searchText, JSON.stringify(extraParams ?? {})],
    );

    return usePagination<T>({
        fetchFn,
        pageSize,
        deepLink: { page, setPage, searchParams, setSearchParams, ignoreParams: ["page", "edit"] },
    });
}
